import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { Select, TextInput } from '../../components/Field';
import { DataTable } from '../../components/Table';
import type { Column } from '../../components/Table';
import { Modal } from '../../overlay/Modal';
import { useOverlay } from '../../overlay/context';
import { toast } from '../../components/Toast';
import { useAuthStore } from '../../app/authStore';
import { api, errorMessage } from '../../lib/api';
import { ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, PERMISSION_LABELS, permissionsForRole } from '@shared/roles';
import { USER_NAME_MAX, generatePassword, validateUserInput } from '@shared/validate';
import { formatDateTime, formatRelativeTime, userStatusLabel } from '@shared/format';
import type { Paginated, Permission, Role, User, UserStatus } from '@shared/types';

const PAGE_SIZE = 20;

interface UserDraft {
  email: string;
  name: string;
  role: Role;
  status: UserStatus;
  password: string;
}

function emptyDraft(): UserDraft {
  return { email: '', name: '', role: 'viewer', status: 'active', password: generatePassword(18) };
}

/** 用户管理：注册、改角色、停用、重置口令、删除。 */
export function UsersPage() {
  const overlay = useOverlay();
  const me = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.includes('user.manage');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paginated<User> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<UserDraft>(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [roleHelp, setRoleHelp] = useState<Role>('viewer');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<Paginated<User>>('/api/users', {
        query: { page, pageSize: PAGE_SIZE, search, role: roleFilter, status: statusFilter },
      });
      setData(result);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // 搜索条件变化时回到第一页，避免停在空页
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const rows = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  function openCreate() {
    setDraft(emptyDraft());
    setRoleHelp('viewer');
    setFieldErrors({});
    setCreating(true);
  }

  function openEdit(user: User) {
    setDraft({
      email: user.email,
      name: user.name,
      role: user.role,
      status: user.status,
      password: '',
    });
    setRoleHelp(user.role);
    setFieldErrors({});
    setEditing(user);
  }

  async function submitCreate() {
    const errors = validateUserInput(
      { email: draft.email, name: draft.name, role: draft.role, password: draft.password },
      { requirePassword: true },
    );
    if (errors.length > 0) {
      const next: Record<string, string> = {};
      for (const item of errors) next[item.field] = item.message;
      setFieldErrors(next);
      return;
    }
    setSaving(true);
    try {
      await api.post<User>('/api/users', {
        email: draft.email.trim(),
        name: draft.name.trim(),
        role: draft.role,
        status: draft.status,
        password: draft.password,
      });
      toast.ok('用户已创建', `${draft.email} 首次登录后必须修改口令。`);
      setCreating(false);
      void load();
    } catch (cause) {
      const message = errorMessage(cause);
      const details =
        cause && typeof cause === 'object' && 'fieldErrors' in cause
          ? (cause as { fieldErrors: Record<string, string> }).fieldErrors
          : {};
      setFieldErrors(details);
      toast.critical('创建用户失败', message);
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit() {
    if (!editing) return;
    const errors = validateUserInput(
      { email: draft.email, name: draft.name, role: draft.role, password: '' },
      { requirePassword: false },
    );
    if (errors.length > 0) {
      const next: Record<string, string> = {};
      for (const item of errors) next[item.field] = item.message;
      setFieldErrors(next);
      return;
    }
    setSaving(true);
    try {
      await api.patch<User>(`/api/users/${editing.id}`, {
        email: draft.email.trim(),
        name: draft.name.trim(),
        role: draft.role,
        status: draft.status,
      });
      toast.ok('用户已更新');
      setEditing(null);
      void load();
    } catch (cause) {
      const details =
        cause && typeof cause === 'object' && 'fieldErrors' in cause
          ? (cause as { fieldErrors: Record<string, string> }).fieldErrors
          : {};
      setFieldErrors(details);
      toast.critical('更新用户失败', errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  async function resetPassword(user: User, trigger: HTMLElement | null) {
    const confirmed = await overlay.confirm({
      placementKey: 'user:reset-password',
      trigger,
      title: `重置「${user.name}」的口令`,
      description: '将生成一个临时口令，该用户的所有登录状态会立即失效，下次登录必须修改口令。',
      confirmText: '生成并重置',
      cancelText: '取消',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      const result = await api.post<{ password: string }>(`/api/users/${user.id}/password`, {});
      await overlay.alert({
        placementKey: 'user:reset-password:result',
        trigger,
        title: '临时口令已生成',
        description: (
          <span className="flex flex-col gap-1.5">
            <span>请通过安全渠道转达给该用户，平台不会保存明文口令。</span>
            <code className="rounded border border-line bg-surface-2 px-2 py-1 font-mono text-sm break-all text-fg">
              {result.password}
            </code>
          </span>
        ),
        confirmText: '我已记录',
      });
    } catch (cause) {
      toast.critical('重置口令失败', errorMessage(cause));
    }
  }

  async function revokeSessions(user: User, trigger: HTMLElement | null) {
    const confirmed = await overlay.confirm({
      placementKey: 'user:revoke-sessions',
      trigger,
      title: `强制下线「${user.name}」`,
      description: '该用户当前的所有登录会话会立即失效，需要重新登录。',
      confirmText: '强制下线',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.post(`/api/users/${user.id}/sessions/revoke`, {});
      toast.ok('已强制该用户下线');
    } catch (cause) {
      toast.critical('操作失败', errorMessage(cause));
    }
  }

  async function remove(user: User, trigger: HTMLElement | null) {
    const confirmed = await overlay.confirm({
      placementKey: 'user:delete',
      trigger,
      title: `删除用户「${user.name}」`,
      description: '删除后该账号无法恢复，其审计日志仍会保留。',
      confirmText: '确认删除',
      cancelText: '取消',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/users/${user.id}`);
      toast.ok('用户已删除');
      void load();
    } catch (cause) {
      toast.critical('删除用户失败', errorMessage(cause));
    }
  }

  const columns = useMemo<Column<User>[]>(() => {
    const base: Column<User>[] = [
      {
        key: 'name',
        header: '姓名',
        render: (user) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-fg">{user.name}</span>
            {user.id === me?.id ? <Badge tone="accent">我自己</Badge> : null}
          </div>
        ),
      },
      {
        key: 'email',
        header: '邮箱',
        render: (user) => <span className="font-mono text-xs text-fg-muted">{user.email}</span>,
      },
      {
        key: 'role',
        header: '角色',
        render: (user) => (
          <span title={ROLE_DESCRIPTIONS[user.role]}>
            <Badge tone={user.role === 'admin' ? 'accent' : 'neutral'}>{ROLE_LABELS[user.role]}</Badge>
          </span>
        ),
      },
      {
        key: 'status',
        header: '状态',
        render: (user) => (
          <Badge tone={user.status === 'active' ? 'ok' : 'caution'}>{userStatusLabel(user.status)}</Badge>
        ),
      },
      {
        key: 'lastLoginAt',
        header: '最近登录',
        hideOnMobile: true,
        render: (user) => (
          <span className="text-xs text-fg-muted" title={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '从未登录'}>
            {user.lastLoginAt ? formatRelativeTime(Date.parse(user.lastLoginAt), Date.now()) : '从未登录'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: '创建时间',
        hideOnMobile: true,
        render: (user) => <span className="text-xs text-fg-muted">{formatDateTime(user.createdAt)}</span>,
      },
    ];

    if (canManage) {
      base.push({
        key: 'actions',
        header: '操作',
        align: 'right',
        render: (user) => (
          <div className="flex justify-end gap-1.5">
            <Button size="sm" onClick={() => openEdit(user)}>
              编辑
            </Button>
            <Button size="sm" onClick={(event) => void resetPassword(user, event.currentTarget)}>
              重置口令
            </Button>
            <Button size="sm" onClick={(event) => void revokeSessions(user, event.currentTarget)}>
              强制下线
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={user.id === me?.id}
              title={user.id === me?.id ? '不能删除自己的账号' : undefined}
              onClick={(event) => void remove(user, event.currentTarget)}
            >
              删除
            </Button>
          </div>
        ),
      });
    }

    return base;
  }, [canManage, me?.id]);

  const activeRole = creating ? draft.role : roleHelp;
  const rolePermissionList: Permission[] = permissionsForRole(activeRole);

  return (
    <div className="flex flex-col gap-3 p-3 lg:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <TextInput
            aria-label="搜索用户"
            placeholder="搜索姓名或邮箱"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-40">
          <Select
            aria-label="按角色筛选"
            placeholder="全部角色"
            options={ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
          />
        </div>
        <div className="w-full sm:w-36">
          <Select
            aria-label="按状态筛选"
            placeholder="全部状态"
            options={[
              { value: 'active', label: '启用' },
              { value: 'suspended', label: '已停用' },
            ]}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          {canManage ? (
            <Button variant="primary" onClick={openCreate}>
              新建用户
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <Card tone="critical">
          <CardBody>
            <p className="text-sm text-fg">{error}</p>
          </CardBody>
        </Card>
      ) : null}

      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(user) => user.id}
        loading={loading}
        emptyTitle="没有匹配的用户"
        emptyDescription="调整筛选条件，或新建一个用户。"
      />

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-fg-muted">
          共 {data?.total ?? 0} 位用户 · 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <Button size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
            上一页
          </Button>
          <Button size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>
            下一页
          </Button>
        </div>
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="新建用户"
        description="新建用户会得到一个临时口令，首次登录时必须修改。"
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCreating(false)}>取消</Button>
            <Button variant="primary" loading={saving} onClick={() => void submitCreate()}>
              创建用户
            </Button>
          </div>
        }
      >
        <UserForm
          draft={draft}
          errors={fieldErrors}
          showPassword
          onChange={setDraft}
          onRoleFocus={setRoleHelp}
        />
      </Modal>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `编辑用户「${editing.name}」` : '编辑用户'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(null)}>取消</Button>
            <Button variant="primary" loading={saving} onClick={() => void submitEdit()}>
              保存修改
            </Button>
          </div>
        }
      >
        <UserForm
          draft={draft}
          errors={fieldErrors}
          showPassword={false}
          onChange={setDraft}
          onRoleFocus={setRoleHelp}
        />
      </Modal>

      <Card>
        <CardHeader>
          <CardTitle subtitle={`${ROLE_LABELS[activeRole]}：${ROLE_DESCRIPTIONS[activeRole]}`}>
            角色权限对照
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
            {rolePermissionList.map((permission) => (
              <li key={permission} className="flex items-center gap-2 text-xs text-fg-muted">
                <span aria-hidden className="size-1.5 rounded-full bg-accent" />
                {PERMISSION_LABELS[permission]}
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}

interface UserFormProps {
  draft: UserDraft;
  errors: Record<string, string>;
  showPassword: boolean;
  onChange: (draft: UserDraft) => void;
  onRoleFocus: (role: Role) => void;
}

function UserForm({ draft, errors, showPassword, onChange, onRoleFocus }: UserFormProps) {
  return (
    <div className="flex flex-col gap-3">
      <TextInput
        label="姓名"
        required
        maxLength={USER_NAME_MAX}
        error={errors.name}
        hint={`最多 ${USER_NAME_MAX} 个字符`}
        value={draft.name}
        onChange={(event) => onChange({ ...draft, name: event.target.value })}
      />
      <TextInput
        label="邮箱"
        required
        type="email"
        error={errors.email}
        value={draft.email}
        onChange={(event) => onChange({ ...draft, email: event.target.value })}
      />
      <Select
        label="角色"
        required
        error={errors.role}
        options={ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
        value={draft.role}
        onChange={(event) => {
          const role = event.target.value as Role;
          onRoleFocus(role);
          onChange({ ...draft, role });
        }}
      />
      <p className="text-xs text-fg-subtle">{ROLE_DESCRIPTIONS[draft.role]}</p>
      <Select
        label="状态"
        options={[
          { value: 'active', label: '启用' },
          { value: 'suspended', label: '已停用' },
        ]}
        value={draft.status}
        onChange={(event) => onChange({ ...draft, status: event.target.value as UserStatus })}
      />
      {showPassword ? (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextInput
              label="初始口令"
              required
              error={errors.password}
              hint="该口令仅用于首次登录，用户登录后必须修改。"
              value={draft.password}
              onChange={(event) => onChange({ ...draft, password: event.target.value })}
            />
          </div>
          <Button onClick={() => onChange({ ...draft, password: generatePassword(18) })}>
            重新生成
          </Button>
        </div>
      ) : null}
    </div>
  );
}
